import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Clock, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/auth/AuthContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useTranslation } from "@/i18n";
import { toast } from "sonner";

export default function Login() {
  const { t } = useTranslation();
  const { login, loginWithCode, register, forgotPassword, formatApiError } = useAuth();
  const [params] = useSearchParams();
  const resetDone = params.get("reset") === "success";
  const [view, setView] = useState("login"); // login | register | forgot | code
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [recoveryMessage, setRecoveryMessage] = useState("");

  // shared name fields
  const [firstName, setFirstName] = useState("");
  const [secondName, setSecondName] = useState("");
  const [alias, setAlias] = useState("");
  const [password, setPassword] = useState("");

  // register-only
  const [recoveryEmail, setRecoveryEmail] = useState("");

  // forgot-only
  const [forgotEmail, setForgotEmail] = useState("");

  // code-only
  const [code, setCode] = useState("");

  const goView = (v) => { setView(v); setError(""); setRecoveryMessage(""); };

  const submit = async () => {
    setError("");
    setBusy(true);
    try {
      if (view === "login") {
        await login({ firstName: firstName.trim(), secondName: secondName.trim(), alias: alias.trim(), password });
      } else if (view === "register") {
        await register({
          firstName: firstName.trim(),
          secondName: secondName.trim(),
          alias: alias.trim(),
          recoveryEmail: recoveryEmail.trim(),
          password,
        });
      } else if (view === "code") {
        await loginWithCode({
          firstName: firstName.trim(),
          secondName: secondName.trim(),
          alias: alias.trim(),
          password,
          code: code.trim(),
        });
        toast.success(t("auth.codeApplied"));
      } else {
        const msg = await forgotPassword(forgotEmail.trim());
        setRecoveryMessage(msg || t("auth.forgotSafeMessage"));
      }
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const titles = {
    login: { title: t("auth.loginTitle"), sub: t("auth.loginSubtitle") },
    register: { title: t("auth.registerTitle"), sub: t("auth.registerSubtitle") },
    forgot: { title: t("auth.forgotTitle"), sub: t("auth.forgotSubtitle") },
    code: { title: t("auth.codeTitle"), sub: t("auth.codeSubtitle") },
  };

  return (
    <div className="min-h-screen bg-background transition-colors duration-300 flex flex-col">
      <header className="sticky top-0 z-20 backdrop-blur-xl bg-card/80 border-b border-border">
        <div className="mx-auto max-w-md px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-lg bg-slate-900 flex items-center justify-center">
              <Clock className="h-3.5 w-3.5 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-display text-base font-semibold tracking-tight text-slate-900">Chronicles</span>
          </div>
          <LanguageSwitcher />
        </div>
      </header>

      <main className="flex-1 mx-auto w-full max-w-md px-5 py-10">
        <p className="text-[11px] uppercase tracking-[0.24em] text-slate-500 mb-3">{t("auth.appTagline")}</p>
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">{titles[view].title}</h1>
        <p className="mt-2 text-sm text-slate-600">{titles[view].sub}</p>

        {resetDone && view === "login" ? (
          <div className="mt-4 flex items-start gap-2 text-sm text-emerald-700 bg-emerald-50 rounded-xl px-3 py-2.5" data-testid="login-reset-success">
            <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{t("auth.resetSuccess")}</span>
          </div>
        ) : null}

        {view === "login" || view === "register" ? (
          <div className="mt-6 flex gap-1.5 rounded-full bg-slate-100 p-1" data-testid="auth-tabs">
            <button
              className={["flex-1 h-10 rounded-full text-sm font-medium transition", view === "login" ? "bg-white shadow text-slate-900" : "text-slate-600"].join(" ")}
              onClick={() => goView("login")}
              data-testid="auth-tab-login"
            >
              {t("auth.tabLogin")}
            </button>
            <button
              className={["flex-1 h-10 rounded-full text-sm font-medium transition", view === "register" ? "bg-white shadow text-slate-900" : "text-slate-600"].join(" ")}
              onClick={() => goView("register")}
              data-testid="auth-tab-register"
            >
              {t("auth.tabRegister")}
            </button>
          </div>
        ) : null}

        <form className="mt-6 space-y-4" onSubmit={(e) => { e.preventDefault(); submit(); }}>
          {view === "forgot" ? (
            <>
              <Field label={t("auth.recoveryEmail")}>
                <Input type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} className="rounded-xl h-12" data-testid="auth-forgot-email" />
              </Field>
              {recoveryMessage ? (
                <p className="text-sm text-emerald-700 bg-emerald-50 rounded-xl px-3 py-2" data-testid="auth-forgot-message">{recoveryMessage}</p>
              ) : null}
            </>
          ) : (
            <>
              <Field label={t("auth.firstName")}>
                <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} className="rounded-xl h-12" data-testid="auth-firstName" />
              </Field>
              <Field label={t("auth.secondName")}>
                <Input value={secondName} onChange={(e) => setSecondName(e.target.value)} className="rounded-xl h-12" data-testid="auth-secondName" />
              </Field>
              <Field label={t("auth.alias")} hint={view === "register" ? t("auth.aliasHint") : undefined}>
                <Input value={alias} onChange={(e) => setAlias(e.target.value)} placeholder={view === "register" ? "Coach" : undefined} className="rounded-xl h-12" data-testid="auth-alias" />
              </Field>
              {view === "register" ? (
                <Field label={t("auth.recoveryEmail")} hint={t("auth.recoveryEmailHint")}>
                  <Input type="email" value={recoveryEmail} onChange={(e) => setRecoveryEmail(e.target.value)} className="rounded-xl h-12" data-testid="auth-recoveryEmail" />
                </Field>
              ) : null}
              <Field label={t("auth.password")}>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-xl h-12" data-testid="auth-password" />
              </Field>
              {view === "code" ? (
                <Field label={t("auth.code")} hint={t("auth.codeHint")}>
                  <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="ASSIST-XXXXXXXX" className="rounded-xl h-12 font-mono-tab" data-testid="auth-code" />
                </Field>
              ) : null}
            </>
          )}

          {error ? <p className="text-sm text-rose-600" data-testid="auth-error">{error}</p> : null}

          <Button type="submit" className="rounded-full h-12 w-full bg-slate-900 hover:bg-slate-800" disabled={busy} data-testid="auth-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {view === "login" ? t("auth.loginButton") : view === "register" ? t("auth.registerButton") : view === "code" ? t("auth.codeSubmit") : t("auth.forgotSubmit")}
          </Button>
        </form>

        <div className="mt-4 text-center space-y-2">
          {view === "login" ? (
            <>
              <button className="block w-full text-sm text-slate-500 hover:text-slate-900 underline" onClick={() => goView("forgot")} data-testid="auth-forgot-link">
                {t("auth.forgotLink")}
              </button>
              <button className="block w-full text-sm text-slate-500 hover:text-slate-900 underline" onClick={() => goView("code")} data-testid="auth-have-code-link">
                {t("auth.haveCode")}
              </button>
            </>
          ) : (
            <button className="text-sm text-slate-500 hover:text-slate-900 underline" onClick={() => goView("login")} data-testid="auth-back-to-login">
              {t("auth.backToLogin")}
            </button>
          )}
        </div>
      </main>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-medium">{label}</Label>
      {children}
      {hint ? <p className="text-[11px] text-slate-400">{hint}</p> : null}
    </div>
  );
}
